import { useState, useEffect, useRef, useContext } from 'react';
import apiUser from '../../apiUser';
import { UserContext } from '../../contexts/userProvider';
import LoadingSpinner from '../Loader/load';
import Relatorios from './Relatorios';
import { avisoDe } from './format';

const PLACEHOLDERS = '{{cliente}} {{competencia}} {{valor_total}} {{tabela_itens}} {{prazo}} {{link}} {{mensagem}}';

const Configuracao = () => {
    const { setAlert } = useContext(UserContext);
    const [cfg, setCfg] = useState(null);
    const [salvando, setSalvando] = useState(false);
    const [testando, setTestando] = useState(false);
    const [preview, setPreview] = useState(null);
    const debounceRef = useRef(null);

    const aviso = avisoDe(setAlert);

    const atualizarPreview = async (cfgAtual) => {
        const c = cfgAtual || cfg;
        if (!c) return;
        try {
            const { data } = await apiUser.post('/faturamento/previewEmail', {
                email_assunto: c.email_assunto,
                email_mensagem: c.email_mensagem,
                email_template: c.email_template,
                prazo_horas: c.prazo_horas,
            });
            if (data.success) setPreview(data.dados);
            else aviso(data.message || 'Erro ao gerar preview.');
        } catch { aviso('Erro ao gerar preview do e-mail.'); }
    };

    useEffect(() => {
        apiUser.get('/faturamento/config').then(({ data }) => {
            if (data.success) {
                setCfg(data.dados);
                atualizarPreview(data.dados);
            } else aviso(data.message);
        }).catch(() => aviso('Erro ao carregar configuração.'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!cfg) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => atualizarPreview(), 800);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cfg?.email_assunto, cfg?.email_mensagem, cfg?.email_template, cfg?.prazo_horas]);

    const salvar = async () => {
        setSalvando(true);
        try {
            const { data } = await apiUser.post('/faturamento/config', {
                prazo_horas: Number(cfg.prazo_horas),
                envio_dia: Number(cfg.envio_dia),
                envio_hora: Number(cfg.envio_hora),
                envio_ativo: cfg.envio_ativo,
                faturar_auto: cfg.faturar_auto,
                email_assunto: cfg.email_assunto,
                email_mensagem: cfg.email_mensagem,
                email_template: cfg.email_template,
                email_notificacao: cfg.email_notificacao,
            });
            aviso(data.message, data.success ? 'success' : 'warning');
            if (data.success) setCfg(data.dados);
        } catch { aviso('Erro ao salvar.'); }
        setSalvando(false);
    };

    const enviarTeste = async () => {
        setTestando(true);
        try {
            const { data } = await apiUser.post('/faturamento/testeEmail', {
                email_assunto: cfg.email_assunto, email_mensagem: cfg.email_mensagem, email_template: cfg.email_template,
            });
            aviso(data.message, data.success ? 'success' : 'warning');
        } catch { aviso('Erro ao enviar teste.'); }
        setTestando(false);
    };

    if (!cfg) return <LoadingSpinner />;

    const horaLabel = (h) => `${String(h).padStart(2, '0')}:00`;

    const fraseResumo = cfg.envio_ativo === '1'
        ? `✅ Disparo automático: todo dia ${cfg.envio_dia} às ${horaLabel(cfg.envio_hora)}, prazo de ${cfg.prazo_horas}h para aprovação. ${cfg.faturar_auto === '1'
            ? 'Sem resposta no prazo, o sistema fatura sozinho.'
            : '🛟 Sem resposta no prazo, fica aguardando você faturar manualmente (não fatura sozinho).'}`
        : "⏸ Disparo automático desativado — use o botão 'Disparar competência' no Painel quando quiser enviar.";

    const campo = (label, el) => (
        <div className='mb-3'>
            <label className='block text-sm font-semibold mb-1 dark:text-white'>{label}</label>
            {el}
        </div>
    );

    return (
        <div className='w-full max-w-3xl mx-auto'>

            {/* Seção: Agendamento do disparo */}
            <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4'>
                <h3 className='text-base font-bold mb-3 dark:text-white'>Agendamento do disparo</h3>
                <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
                    {campo('Dia do mês',
                        <select className='border rounded-lg p-2 w-full dark:bg-gray-600 dark:text-white'
                            value={String(cfg.envio_dia ?? '')} onChange={(e) => setCfg({ ...cfg, envio_dia: e.target.value })}>
                            {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                                <option key={d} value={String(d)}>{d}</option>
                            ))}
                        </select>
                    )}
                    {campo('Hora',
                        <select className='border rounded-lg p-2 w-full dark:bg-gray-600 dark:text-white'
                            value={String(cfg.envio_hora ?? '')} onChange={(e) => setCfg({ ...cfg, envio_hora: e.target.value })}>
                            {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                                <option key={h} value={String(h)}>{horaLabel(h)}</option>
                            ))}
                        </select>
                    )}
                    {campo('Envio automático',
                        <select className='border rounded-lg p-2 w-full dark:bg-gray-600 dark:text-white'
                            value={String(cfg.envio_ativo ?? '0')} onChange={(e) => setCfg({ ...cfg, envio_ativo: e.target.value })}>
                            <option value='0'>Desativado</option>
                            <option value='1'>Ativado</option>
                        </select>
                    )}
                    {campo('Prazo de aprovação (horas)',
                        <input type='number' className='border rounded-lg p-2 w-full dark:bg-gray-600 dark:text-white'
                            value={cfg.prazo_horas ?? ''} onChange={(e) => setCfg({ ...cfg, prazo_horas: e.target.value })} />
                    )}
                    {campo('Faturar sem resposta',
                        <select className='border rounded-lg p-2 w-full dark:bg-gray-600 dark:text-white'
                            value={String(cfg.faturar_auto ?? '0')} onChange={(e) => setCfg({ ...cfg, faturar_auto: e.target.value })}>
                            <option value='0'>Eu faturo (recomendado no início)</option>
                            <option value='1'>Faturar sozinho</option>
                        </select>
                    )}
                </div>
                <p className='mt-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-600 rounded-lg px-3 py-2'>
                    {fraseResumo}
                </p>
            </div>

            {/* Seção: Conteúdo do e-mail */}
            <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4'>
                <h3 className='text-base font-bold mb-3 dark:text-white'>Conteúdo do e-mail</h3>
                {campo('Assunto',
                    <input className='border rounded-lg p-2 w-full dark:bg-gray-600 dark:text-white'
                        value={cfg.email_assunto || ''} onChange={(e) => setCfg({ ...cfg, email_assunto: e.target.value })} />
                )}
                {campo('Mensagem',
                    <textarea rows={3} className='border rounded-lg p-2 w-full dark:bg-gray-600 dark:text-white'
                        value={cfg.email_mensagem || ''} onChange={(e) => setCfg({ ...cfg, email_mensagem: e.target.value })} />
                )}
                <div className='mb-3'>
                    <label className='block text-sm font-semibold mb-1 dark:text-white'>Template HTML</label>
                    <p className='text-xs text-gray-500 dark:text-gray-400 mb-1'>
                        Placeholders disponíveis: <code className='bg-gray-100 dark:bg-gray-600 px-1 rounded text-xs'>{PLACEHOLDERS}</code>
                    </p>
                    <p className='text-xs text-gray-500 dark:text-gray-400 mb-1'>Deixe vazio para usar o template padrão do sistema.</p>
                    <textarea rows={8} className='border rounded-lg p-2 w-full font-mono text-xs dark:bg-gray-600 dark:text-white'
                        value={cfg.email_template || ''} onChange={(e) => setCfg({ ...cfg, email_template: e.target.value })} />
                </div>
                <div className='mb-3'>
                    <label className='block text-sm font-semibold mb-1 dark:text-white'>E-mail para avisos internos (contestação / resumo do disparo)</label>
                    <input className='border rounded-lg p-2 w-full dark:bg-gray-600 dark:text-white'
                        value={cfg.email_notificacao || ''} onChange={(e) => setCfg({ ...cfg, email_notificacao: e.target.value })} />
                    <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
                        Quando o cliente não tem remetente vinculado, os avisos vão para este endereço. Vazio = não envia.
                    </p>
                </div>
            </div>

            {/* Seção: Relatórios disponíveis */}
            <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4'>
                <h3 className='text-base font-bold mb-1 dark:text-white'>Relatórios disponíveis para o disparo</h3>
                <p className='text-xs text-gray-500 dark:text-gray-400 mb-3'>
                    Novos tipos de relatório serão adicionados em versões futuras.
                </p>
                <Relatorios />
            </div>

            {/* Seção: Preview do e-mail */}
            <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4'>
                <h3 className='text-base font-bold mb-3 dark:text-white'>Preview do e-mail</h3>
                <div className='flex gap-2 mb-3'>
                    <button onClick={() => atualizarPreview()}
                        className='px-4 py-2 bg-gray-200 dark:bg-gray-600 dark:text-white rounded-lg text-sm'>
                        Atualizar preview
                    </button>
                </div>
                {preview ? (
                    <div className='border rounded-lg overflow-hidden bg-white'>
                        <div className='px-4 py-2 bg-gray-100 dark:bg-gray-600 text-sm dark:text-white border-b'>
                            <b>Assunto:</b> {preview.assunto}
                        </div>
                        <iframe title='Preview do e-mail' sandbox='' srcDoc={preview.html}
                            className='w-full bg-white' style={{ height: '640px', border: 'none' }} />
                    </div>
                ) : (
                    <p className='text-sm text-gray-500 dark:text-gray-400'>Carregando preview...</p>
                )}
            </div>

            {/* Rodapé */}
            <div className='flex items-center'>
                <button onClick={salvar} disabled={salvando}
                    className='px-6 py-2 bg-vox-blue-600 hover:bg-vox-blue-700 text-white rounded-lg disabled:opacity-50'>
                    {salvando ? 'Salvando...' : 'Salvar configuração'}
                </button>
                <button onClick={enviarTeste} disabled={testando}
                    className='ml-3 px-6 py-2 bg-gray-200 dark:bg-gray-600 dark:text-white rounded-lg disabled:opacity-50'>
                    {testando ? 'Enviando...' : 'Enviar teste para meu e-mail'}
                </button>
            </div>
        </div>
    );
};

export default Configuracao;
